import { LoadingStateChart } from 'components/analytics';
import { ChartBase } from 'components/common';
import { ExportIcon } from 'components/icons';
import { CardBackground } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import type { PurchaseTiming } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';

const ExportMenuLazy = lazy(() => import('./PurchaseTimingExportMenu'));

interface PurchaseTimingOrders2And3Props {
  data: PurchaseTiming | undefined;
  loading: boolean;
}

export default function PurchaseTimingOrders2And3({
  data,
  loading,
}: PurchaseTimingOrders2And3Props) {
  const exportContainerRef = useRef<HTMLDivElement>(null);

  /** Start Cards Data */
  const third_growth = data?.third_growth
    ? Object.keys(data.third_growth)[0]
    : '';
  const third_choke = data?.third_choke ? Object.keys(data.third_choke)[0] : '';
  const third_growth_perc = data?.third_growth
    ? Number(Object.values(data?.third_growth)[0].toFixed(2))
    : 0;
  const third_choke_perc = data?.third_choke
    ? Object.values(data.third_choke)[0]
    : 0;

  // Ends Cards Data

  /** Chart Data Starts */

  // Step to convert from days to weeks
  const step = 7;

  // Thresholds is used as reference to slice the dataset into categories and display at different colors

  const minThreshold = Math.ceil(parseInt(third_growth) / step) ?? 1;
  const maxThreshold = Math.ceil(parseInt(third_choke) / step) ?? 1;

  //  baseData converts the original data based on the steps (eg. from days to weeks)
  const baseData = Object.entries(data?.third_order_timing || {}).map(
    ([key, value]) => {
      const newKey = Math.ceil(parseInt(key) / step);
      return {
        [newKey]: value,
      };
    }
  );

  // Subsets of basedata based on the original and threshold settings
  const active = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key <= minThreshold;
        })
      ),
    [minThreshold, baseData]
  );

  const atRisk = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key > minThreshold && key < maxThreshold;
        })
      ),
    [baseData, minThreshold, maxThreshold]
  );

  const churn = useMemo(
    () =>
      Object.assign(
        {},
        ...baseData.filter((item) => {
          const key = parseInt(Object.keys(item)[0]);
          return key >= maxThreshold;
        })
      ),
    [baseData, maxThreshold]
  );

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'Active',
          backgroundColor: '#4F92F7',
          borderColor: '#4F92F7',
          data: active,
        },
        {
          label: 'At risk',
          backgroundColor: '#F87349',
          borderColor: '#F87349',
          data: atRisk,
        },
        {
          label: 'Churned',
          backgroundColor: '#D5CBB7',
          borderColor: '#D5CBB7',
          data: churn,
        },
      ],
    }),
    [active, atRisk, churn]
  );

  const options = {
    interaction: {
      mode: 'point',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          text: 'Weeks from 2nd order',
          display: true,
          font: { size: 15 },
        },
        stacked: true,
        ticks: {
          autoSkip: false,
        },
      },
      y: {
        ticks: {
          callback: (value: number) =>
            (value / 100).toLocaleString('en-US', {
              style: 'percent',
              minimumFractionDigits: value <= 1 ? 2 : 0,
              maximumFractionDigits: value <= 1 ? 2 : 0,
            }),
        },
        title: { text: '% purchased', display: true, font: { size: 15 } },
      },
    },
    plugins: {
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 20,
          font: {
            size: 15,
            lineHeight: '1.5px',
          },
        },
      },
      tooltip: {
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: (tooltip: { parsed: { y: string } }) => tooltip.parsed.y + '%',
        },
      },
    },
  };

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Weeks', key: 'period' },
      { label: 'Active', key: 'active' },
      { label: 'At risk', key: 'at_risk' },
      { label: 'Churn', key: 'churn' },
    ];

    const activeCSV = Object.entries(active).map(([key, value]) => ({
      period: key,
      active: value as number,
    }));

    const atRiskCSV = Object.entries(atRisk).map(([key, value]) => ({
      period: key,
      at_risk: value as number,
    }));

    const churnCSV = Object.entries(churn).map(([key, value]) => ({
      period: key,
      churn: value as number,
    }));

    const csvData = [...activeCSV, ...atRiskCSV, ...churnCSV];

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [active, atRisk, churn]);

  const { currentStore } = useAppSelector(userDetailsState);
  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-purchase_timing-first_24_hours-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  if (loading) return <LoadingStateChart chartTitle="Between orders 2 and 3" />;
  if (!data?.third_order_timing) return null;

  return (
    <div className="py-6" ref={exportContainerRef}>
      <div className="flex items-end justify-between px-2">
        <div className="whitespace-nowrap">
          <p className="text-2xl leading-7 whitespace-nowrap">
            Between 2nd and 3rd order
          </p>
          <p className="mt-2 text-base whitespace-nowrap">
            The graph below represents the distribution of when Repeats convert
            to Loyals.
          </p>
        </div>

        <Suspense
          fallback={
            <div className="flex items-center px-5 py-2.5 space-x-2 rounded-lg bg-segments-gray focus:outline-none">
              <p>Export</p>
              <ExportIcon className="w-5 h-5" />
            </div>
          }
        >
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Suspense>
      </div>

      <div className="mt-4">
        <CardBackground>
          <div className="p-10">
            <div className="flex justify-between">
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  Day {third_growth}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Most converted by
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {third_growth_perc ?? '--'} %
                </p>
                <p className="text-base text-black text-opacity-50">
                  Day {third_growth} conversion
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  Day {third_choke}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Conversion slow after
                </p>
              </div>
              <div className="space-y-1">
                <p className="leading-9 text-segments-32px">
                  {data.third_adp ? data.third_adp : '--'}
                </p>
                <p className="text-base text-black text-opacity-50">
                  Avg. days
                </p>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-base text-black text-opacity-50">
                <span className="font-bold">
                  {(third_growth_perc ?? '--') + '% '}
                </span>
                of loyal customers purchase their third order within
                <span className="font-bold"> {third_growth} </span>
                days after the second order. After that, an additional
                <span className="font-bold">
                  {' '}
                  {third_choke_perc - third_growth_perc > 0
                    ? (third_choke_perc - third_growth_perc).toFixed(2) + '%'
                    : '--'}{' '}
                </span>
                of orders happen between days
                <span className="font-bold"> {third_growth} </span> and
                <span className="font-bold"> {third_choke} </span> from the
                first order.
                {data.third_adp && (
                  <span>
                    The average days between orders 1 and 2 is
                    <span className="font-bold"> {data.third_adp} </span> days
                  </span>
                )}
              </p>
            </div>
            <hr className="my-8" />
            <ChartBase type="bar" data={chartData} options={options} />
          </div>
        </CardBackground>
      </div>
    </div>
  );
}
